import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Phone, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const nav = useNavigate();
  const { loginUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ mobile: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) return toast.error("Enter a valid 10-digit Indian mobile number");
    if (!form.password) return toast.error("Enter your password");
    setBusy(true);
    try {
      const u = await loginUser(form.mobile.trim(), form.password);
      toast.success(`Welcome back, ${u.full_name || "teacher"}!`);
      nav("/profile");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Login failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="login-page">
      <div>
        <div className="chip chip-green"><LogIn size={14}/> Sign in</div>
        <h1 className="mt-2 font-display text-3xl font-extrabold text-slate-900">Welcome back</h1>
        <p className="mt-1 text-slate-600">Sign in with your registered mobile number.</p>
      </div>

      <form onSubmit={submit} className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="l-mobile" className="text-sm font-semibold flex items-center gap-1"><Phone size={12}/> Mobile number</Label>
          <Input id="l-mobile" value={form.mobile}
                 onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                 className="h-11 mt-1.5" placeholder="10-digit Indian mobile" inputMode="numeric" maxLength={10}
                 data-testid="login-mobile" autoFocus/>
        </div>
        <div>
          <Label htmlFor="l-pw" className="text-sm font-semibold flex items-center gap-1"><Lock size={12}/> Password</Label>
          <div className="mt-1.5 relative">
            <Input id="l-pw" type={show ? "text" : "password"} value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="h-11 pr-10" placeholder="Your password" data-testid="login-password"/>
            <button type="button" onClick={() => setShow((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800" data-testid="login-toggle-pw">
              {show ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
          <div className="text-right mt-1">
            <Link to="/forgot-password" className="text-xs text-emerald-700 font-semibold hover:underline" data-testid="login-forgot">
              Forgot password?
            </Link>
          </div>
        </div>

        <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"} <LogIn size={16}/>
        </Button>

        <div className="text-sm text-slate-600 text-center pt-2 border-t border-slate-100">
          New here?{" "}
          <Link to="/register" className="text-emerald-700 font-semibold hover:underline" data-testid="login-to-register">
            Create a free account
          </Link>
        </div>
      </form>
    </div>
  );
}
